// IMPORT (HARDEN): the in-memory API's guided imports (services/api/src/routes/import-runs.ts),
// with the real routes' rules: owners only (403 for admins, operators and everyone else; 404
// outside the company), a Zulip import needs the `chat` add-on, the mapping names every preview
// key and is fixed once the import started, one import queued or running at a time, cancel
// before the run (at once) or while it runs (at its next batch).
//
// Files: a ClickUp CSV export is read for real (`planClickUpCsv`, the API's planner), so the
// preview is the file's. A Zulip export is a binary `.tar.gz` the twin cannot unpack: any
// gzip / tar upload answers the canned preview of the API's fixture export
// (services/api/test/fixtures/import/zulip-export: #general, #leadership, #random; Ana Lima
// matched to the owner by email). The background job is simulated: each read of a queued or
// running import moves it on (queued → running → done in two steps), then `succeeded` with a
// report from its dry run.
import { planClickUpCsv, previewCsv } from "@companyos/domain-work/import";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem, validation } from "./fake";
import { uid } from "./fake-data";

type Run = OperationOutput<"importRuns.get">;
type Preview = Run["preview"];
type Mapping = Run["mapping"];
type DryRun = OperationOutput<"importRuns.dryRun">;
type Tally = Record<string, number>;

const forbidden = () => problem(403, "forbidden", "Forbidden", "owners only");
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
const badFile = (code: string, message: string) =>
  json(
    {
      type: "about:blank",
      title: "Validation failed",
      status: 422,
      code: "validation_failed",
      detail: message,
      errors: [{ path: "file", code, message }],
    },
    422,
  );

/** The canned preview of the API's Zulip fixture export (the owner matched by email). */
function zulipPreview(now: Date, owner: { id: string; name: string; email: string }) {
  const day = 86_400_000;
  const preview: Preview = {
    source: "zulip",
    containers: [
      {
        key: "zulip:space",
        kind: "space",
        name: "Zulip archive",
        parent_key: null,
        is_private: false,
        items: 5,
        topics: 0,
      },
      {
        key: "zulip:1",
        kind: "channel",
        name: "general",
        parent_key: "zulip:space",
        is_private: false,
        items: 3,
        topics: 2,
      },
      {
        key: "zulip:2",
        kind: "channel",
        name: "leadership",
        parent_key: "zulip:space",
        is_private: true,
        items: 1,
        topics: 1,
      },
      {
        key: "zulip:3",
        kind: "channel",
        name: "random",
        parent_key: "zulip:space",
        is_private: false,
        items: 1,
        topics: 1,
      },
    ],
    people: [
      {
        key: "zulip:user:101",
        name: "Ana Lima",
        email: owner.email,
        items: 2,
        match: { membership_id: owner.id, display_name: owner.name },
      },
      {
        key: "zulip:user:102",
        name: "Bob Builder",
        email: "bob@northwind.example",
        items: 2,
        match: null,
      },
      {
        key: "zulip:user:103",
        name: "Cleo Park",
        email: "cleo@northwind.example",
        items: 1,
        match: null,
      },
    ],
    counts: {
      messages: 7,
      in_window: 5,
      skipped_older: 1,
      skipped_private: 1,
      skipped_deleted_or_unknown: 0,
      duplicates: 0,
      channels: 3,
      topics: 4,
      people: 3,
    },
    window: {
      from: new Date(now.getTime() - 365 * day).toISOString(),
      to: now.toISOString(),
    },
    warnings: [
      "1 messages are older than 12 months: not imported (only counted)",
      "1 direct and group messages are never imported",
      "Imported channels are archived (read-only history) and their topics resolved; messages notify no one.",
    ],
  };
  const mapping: Mapping = {
    containers: {
      "zulip:space": { action: "create", name: "Zulip archive" },
      "zulip:1": { action: "create", name: "general" },
      "zulip:2": { action: "create", name: "leadership" },
      "zulip:3": { action: "create", name: "random" },
    },
    people: {
      "zulip:user:101": { action: "match", membership_id: owner.id },
      "zulip:user:102": { action: "skip" },
      "zulip:user:103": { action: "skip" },
    },
  };
  return { preview, mapping };
}

/** A ClickUp CSV export, planned with the API's planner; null when it is not one. */
function clickUpPreview(text: string, people: { id: string; name: string }[]) {
  const csv = previewCsv(text, 20_000);
  const cols = new Set(csv.header.map((h) => h.trim().toLowerCase().replace(/\s+/g, "_")));
  if (!cols.has("task_name") || !cols.has("list_name")) return null;
  const plan = planClickUpCsv(csv.rows);
  if (plan.tasks.length === 0) return null;
  const containers: Mapping["containers"] = {};
  const map: Mapping["people"] = {};
  const byName = new Map(people.map((p) => [p.name.toLowerCase(), p]));
  const preview: Preview = {
    source: "clickup",
    containers: [
      ...plan.spaces.map((s) => {
        containers[s.key] = { action: "create", name: s.name };
        return {
          key: s.key,
          kind: "space" as const,
          name: s.name,
          parent_key: null,
          is_private: false,
          items: s.tasks,
          topics: 0,
        };
      }),
      ...plan.lists.map((l) => {
        containers[l.key] = { action: "create", name: l.name };
        return {
          key: l.key,
          kind: "board" as const,
          name: l.name,
          parent_key: l.spaceKey,
          is_private: false,
          items: l.tasks,
          topics: 0,
        };
      }),
    ],
    people: plan.people.map((p) => {
      const m = byName.get(p.name.toLowerCase()) ?? null;
      map[p.key] = m ? { action: "match", membership_id: m.id } : { action: "skip" };
      return {
        key: p.key,
        name: p.name,
        email: null,
        items: p.tasks,
        match: m ? { membership_id: m.id, display_name: m.name } : null,
      };
    }),
    counts: {
      rows: plan.counts.rows,
      tasks: plan.counts.tasks,
      subtasks: plan.counts.subtasks,
      skipped_no_title: plan.counts.skippedNoTitle,
      spaces: plan.spaces.length,
      boards: plan.lists.length,
      people: plan.people.length,
    },
    window: null,
    warnings: [
      ...plan.warnings.slice(0, 50),
      "ClickUp's CSV export carries no comments, checklists or attachments: tasks import without them.",
    ],
  };
  return { preview, mapping: { containers, people: map } };
}

const bump = (t: Tally, k: string, n = 1) => {
  if (n > 0) t[k] = (t[k] ?? 0) + n;
};

/** The twin's dry run: every target as mapped (an `existing` id is taken as live). */
function dryRunOf(run: Run, imported: boolean): DryRun {
  const created: Tally = {};
  const reused: Tally = {};
  const skipped: Tally = {};
  const action = new Map<string, string>();
  for (const c of run.preview.containers) {
    const want = run.mapping.containers[c.key];
    const parent = c.parent_key ? action.get(c.parent_key) : undefined;
    const a =
      !want || want.action === "skip" || parent === "skip"
        ? "skip"
        : want.action === "existing" || imported
          ? "reuse"
          : "create";
    action.set(c.key, a);
    bump(a === "create" ? created : a === "reuse" ? reused : skipped, `${c.kind}s`);
    if (c.kind === "space") continue;
    const what = run.source === "zulip" ? "messages" : "tasks";
    bump(a === "skip" ? skipped : imported ? reused : created, what, c.items);
    if (c.topics > 0)
      bump(a === "skip" ? skipped : imported ? reused : created, "topics", c.topics);
  }
  for (const p of run.preview.people) {
    const want = run.mapping.people[p.key];
    if (want?.action === "match") bump(imported ? reused : created, "identities");
    else if (want?.action === "invite") bump(imported ? reused : created, "invitations");
    else bump(skipped, "people");
  }
  const warnings =
    run.source === "zulip" &&
    run.preview.people.some((p) => run.mapping.people[p.key]?.action === "skip")
      ? [
          {
            key: null,
            code: "unmapped_senders",
            message:
              "messages from people not matched import as system messages with the sender's name",
          },
        ]
      : [];
  return { ready: true, created, reused, skipped, conflicts: [], warnings };
}

export function createImportHandlers(deps: {
  now: () => Date;
  /** The company's people (`people.list`'s rows): ClickUp assignees match by name. */
  peopleOf: (p: Principal) => { id: string; name: string }[];
  /** Whether the company has an add-on on (`chat` for a Zulip import). */
  featureOn: (p: Principal, feature: string) => boolean;
}): Partial<Record<RouteId, Handler>> {
  const runs = new Map<string, Run[]>();
  /** Runs that succeeded once: a re-run reuses everything. */
  const imported = new Set<string>();
  let seq = 0;
  const at = () => deps.now().toISOString();
  const runsOf = (co: string) => {
    const l = runs.get(co) ?? [];
    runs.set(co, l);
    return l;
  };
  const isOwner = (p: Principal) => p.roles.includes("owner");
  const find = (p: Principal, id: string | undefined) =>
    runsOf(p.company.id).find((r) => r.id === id);
  const summary = (r: Run) => {
    const { preview: _p, mapping: _m, report: _r, ...rest } = r;
    return rest;
  };

  /** The simulated job: each read moves a queued or running import on. */
  function tick(r: Run) {
    if (r.status === "queued") {
      const total = r.preview.containers.reduce(
        (n, c) => n + (c.kind === "space" ? 0 : c.items),
        0,
      );
      Object.assign(r, {
        status: "running",
        started_at: at(),
        progress: { phase: r.source === "zulip" ? "messages" : "tasks", done: 0, total },
      });
    } else if (r.status === "running") {
      if (r.cancel_requested) {
        Object.assign(r, { status: "cancelled", finished_at: at() });
      } else if (r.progress.done < r.progress.total) {
        r.progress = {
          ...r.progress,
          done: Math.min(r.progress.total, r.progress.done + Math.ceil(r.progress.total / 2)),
        };
      } else {
        const d = dryRunOf(r, imported.has(r.id));
        imported.add(r.id);
        Object.assign(r, {
          status: "succeeded",
          finished_at: at(),
          progress: { ...r.progress, phase: "done" },
          report: {
            created: d.created,
            updated: d.reused,
            skipped: d.skipped,
            warnings: d.warnings,
            errors: [],
            started_at: r.started_at ?? at(),
            finished_at: at(),
          },
        });
      }
    }
    r.updated_at = at();
  }

  return {
    "importRuns.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!isOwner(p)) return forbidden();
      for (const r of runsOf(p.company.id)) tick(r);
      return json(page([...runsOf(p.company.id)].reverse().map(summary), url));
    },
    "importRuns.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!isOwner(p)) return forbidden();
      const form = (body ?? {}) as { source?: string; file?: string };
      const source = form.source?.trim();
      if (source !== "zulip" && source !== "clickup") return validation("source: zulip or clickup");
      if (source === "zulip" && !deps.featureOn(p, "chat"))
        return problem(403, "feature_disabled", "Feature disabled", "the chat add-on is off");
      const text = form.file ?? "";
      if (text === "") return badFile("empty", "the file is empty");
      const binary = text.startsWith("\u001f") || text.includes("ustar") || text.includes("\u0000");
      let parsed: { preview: Preview; mapping: Mapping } | null;
      if (source === "zulip") {
        if (!binary)
          return badFile(
            "invalid_type",
            "a Zulip import takes the organization export: a .tar.gz (or .tar) of realm.json and messages-*.json",
          );
        const membership = p.me.memberships.find((m) => m.company_id === p.company.id);
        parsed = zulipPreview(deps.now(), {
          id: membership?.membership_id ?? uid(0x1000, 1),
          name: p.me.user.name,
          email: p.me.user.email,
        });
      } else {
        if (binary)
          return badFile(
            "invalid_type",
            "a ClickUp import takes ClickUp's CSV export (a .csv file)",
          );
        parsed = clickUpPreview(text, deps.peopleOf(p));
        if (!parsed)
          return badFile(
            "invalid_export",
            "not a ClickUp export: no task name or list name column",
          );
      }
      const t = at();
      const membership = p.me.memberships.find((m) => m.company_id === p.company.id);
      const run: Run = {
        id: uid(0x6200, ++seq),
        company_id: p.company.id,
        source,
        status: "uploaded",
        file_name: source === "zulip" ? "zulip-export.tar.gz" : "clickup-export.csv",
        file_bytes: text.length,
        progress: { phase: null, done: 0, total: 0 },
        error: null,
        cancel_requested: false,
        created_by_membership_id: membership?.membership_id ?? null,
        created_at: t,
        updated_at: t,
        queued_at: null,
        started_at: null,
        finished_at: null,
        preview: parsed.preview,
        mapping: parsed.mapping,
        report: null,
      };
      runsOf(p.company.id).push(run);
      return json(run, 201);
    },
    "importRuns.get": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!isOwner(p)) return forbidden();
      const r = find(p, params.id);
      if (!r) return notFound();
      tick(r);
      return json(r);
    },
    "importRuns.updateMapping": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!isOwner(p)) return forbidden();
      const r = find(p, params.id);
      if (!r) return notFound();
      if (r.status !== "uploaded")
        return conflict(`the import is ${r.status}; its mapping is fixed`);
      const m = (body ?? {}) as Mapping;
      const keys = (a: string[], b: object | undefined) =>
        a.length === Object.keys(b ?? {}).length && a.every((k) => b && k in b);
      if (
        !keys(
          r.preview.containers.map((c) => c.key),
          m.containers,
        ) ||
        !keys(
          r.preview.people.map((x) => x.key),
          m.people,
        )
      )
        return validation("mapping: name every container and person of the export");
      for (const x of r.preview.people)
        if (m.people[x.key]?.action === "invite" && !x.email)
          return validation(`people.${x.key}: ${x.name} has no email`);
      r.mapping = m;
      r.updated_at = at();
      return json(r);
    },
    "importRuns.dryRun": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!isOwner(p)) return forbidden();
      const r = find(p, params.id);
      if (!r) return notFound();
      return json(dryRunOf(r, imported.has(r.id)));
    },
    "importRuns.start": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!isOwner(p)) return forbidden();
      const r = find(p, params.id);
      if (!r) return notFound();
      if (r.status === "queued" || r.status === "running")
        return conflict(`the import is ${r.status} already`);
      if (
        runsOf(p.company.id).some(
          (x) => x !== r && (x.status === "queued" || x.status === "running"),
        )
      )
        return conflict("another import is queued or running; one runs at a time");
      Object.assign(r, {
        status: "queued",
        queued_at: at(),
        started_at: null,
        finished_at: null,
        cancel_requested: false,
        error: null,
        report: null,
        progress: { phase: null, done: 0, total: 0 },
        updated_at: at(),
      });
      return json(r, 202);
    },
    "importRuns.cancel": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!isOwner(p)) return forbidden();
      const r = find(p, params.id);
      if (!r) return notFound();
      if (r.status === "uploaded" || r.status === "queued") {
        Object.assign(r, { status: "cancelled", finished_at: at(), updated_at: at() });
        return json(r);
      }
      if (r.status === "running") {
        if (r.cancel_requested) return conflict("a cancel is asked already");
        r.cancel_requested = true;
        return json(r);
      }
      return conflict(`the import is ${r.status}; there is nothing to cancel`);
    },
  };
}
